import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, RefreshCw, Users } from 'lucide-react';
import { adminApi } from '../services/api';

interface VendorLoginRow {
  vendor_no: string;
  user_name: string;
  full_name: string | null;
  email: string | null;
  active: boolean;
  last_login: string | null;
}

/**
 * Vendor logins for the tenant, pushed by Business Central as credentials change
 * (Settled Portal Setup in BC, using the integration key below) and also pulled
 * periodically from its vendorCredentials API as a backstop. This list is what lets a
 * vendor sign in on the shared hostname: their username or email resolves to this
 * tenant and vendor number.
 */
export default function TenantVendors({ tenantId }: { tenantId: number }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId, 'vendors'],
    queryFn: async () => (await adminApi.getTenantVendors(tenantId)).data
  });

  const sync = useMutation({
    mutationFn: () => adminApi.syncTenantVendors(tenantId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['tenant', tenantId, 'vendors'] })
  });

  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const createKey = useMutation({
    mutationFn: () => adminApi.createIntegrationKey(tenantId),
    onSuccess: (response) => {
      setNewKey(response.data.key);
      setCopied(false);
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId, 'vendors'] });
    }
  });

  const onCreateKey = () => {
    if (data?.hasIntegrationKey &&
        !window.confirm('Generating a new key stops the current one from working. Business Central must be updated with the new key. Continue?')) {
      return;
    }
    createKey.mutate();
  };

  const copyKey = async () => {
    if (!newKey) return;
    try {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const vendors: VendorLoginRow[] = data?.vendors || [];
  const syncError = (sync.error as any)?.response?.data?.message || data?.syncError;
  const keyError = (createKey.error as any)?.response?.data?.message;

  return (
    <div className="card mt-6">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5" /> Vendor logins
          </h2>
          <p className="text-sm text-gray-500">
            Synced from Business Central
            {data?.syncedAt ? ` · last sync ${new Date(data.syncedAt).toLocaleString('sv-SE')}` : ' · never synced'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${sync.isPending ? 'animate-spin' : ''}`} />
          {sync.isPending ? 'Syncing...' : 'Sync now'}
        </button>
      </div>

      {/* Business Central integration key */}
      <div className="mx-6 mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <KeyRound className="w-4 h-4" /> Business Central integration key
            </p>
            <p className="text-sm text-gray-500">
              {data?.hasIntegrationKey
                ? `Active since ${new Date(data.integrationKeyCreatedAt).toLocaleString('sv-SE')}. BC pushes credential changes with it.`
                : 'No key yet. Generate one and enter it in Settled Portal Setup in Business Central.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onCreateKey}
            disabled={createKey.isPending}
            className="shrink-0 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            {data?.hasIntegrationKey ? 'Generate new key' : 'Generate key'}
          </button>
        </div>

        {newKey && (
          <div className="mt-3">
            <p className="text-sm text-amber-800 mb-2">
              Copy this key into Business Central now - it is not shown again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded font-mono text-sm break-all">{newKey}</code>
              <button
                type="button"
                onClick={copyKey}
                className="shrink-0 flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-xl hover:bg-gray-100 text-sm"
              >
                <Copy className="w-4 h-4" /> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}

        {keyError && <p className="mt-2 text-sm text-red-700">{keyError}</p>}
      </div>

      {syncError && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
          {syncError}
        </div>
      )}

      <div className="p-6 overflow-x-auto">
        {isLoading ? (
          <p className="text-gray-500">Loading...</p>
        ) : vendors.length === 0 ? (
          <p className="text-gray-500">No vendor logins synced yet.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4 font-medium">Vendor No.</th>
                <th className="py-2 pr-4 font-medium">Username</th>
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 font-medium">Last login</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={`${v.vendor_no}/${v.user_name}`} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-mono">{v.vendor_no}</td>
                  <td className="py-2 pr-4">{v.user_name}</td>
                  <td className="py-2 pr-4">{v.full_name}</td>
                  <td className="py-2 pr-4">{v.email}</td>
                  <td className="py-2 pr-4">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${v.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {v.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2">{v.last_login ? new Date(v.last_login).toLocaleString('sv-SE') : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
